import React, {useEffect, useMemo, useState} from 'react';
import {
    Form,
    FormFeedback,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    Spinner,
    TabContent,
    TabPane,
} from 'reactstrap';
import {getAdvertisers} from "../../api/advertiserApi";
import {getAgencies} from "../../api/agencyApi";
import {getAdNetworks} from "../../api/adNetworkApi";
import * as Yup from "yup";
import {useFormik} from "formik";
import { confirmImportCampaign, importCampaign } from '../../api/campaignApi';
import TableContainer from "../.././components/TableContainerReactTable";
import { format } from 'date-fns';
import { Advertiser } from '../../types/advertiser';
import { Agency } from '../../types/agency';
import { Campaign } from '../../types/campaign';
import { AdNetwork } from '../../types/ad-network';
import { toast } from 'react-toastify';
import { BlockedKeywords } from '../../types/blocked-keywords';
import { getBlockedKeywords } from '../../api/blockedKeywordsApi';

interface AddModalProps {
    isOpen: boolean;
    toggle: () => void;
}

const ImportCampaignModal = ({ isOpen, toggle }: AddModalProps) => {
    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
    const [adNetworks, setAdNetworks] = useState<AdNetwork[]>([]);
    const [palavrasChaveProtegida, setPalavrasChaveProtegida] = useState<BlockedKeywords[]>([]);
    const [activeArrowTab, setActiveArrowTab] = useState<number>(1);
    const [firstStepImportButtonLoader, setFirstStepImportButtonLoader] = useState<boolean>(false);
    const [secondStepImportButtonLoader, setSecondStepImportButtonLoader] = useState<boolean>(false);
    const [campaignToSave, setCampaignToSave] = useState<Campaign|null>(null);

    useEffect(() => {
        // Pega os advertisers, agencies, ad networks e blocked keywords para popular os <select>
        getAdvertisers().then(setAdvertisers);
        getAgencies().then(setAgencies);
        getAdNetworks().then(setAdNetworks);
        getBlockedKeywords().then(setPalavrasChaveProtegida);
    }, []);

    const toggleImportCampaignModal = () => {
        toggle();
        setActiveArrowTab(1);
        handleFirstStepForm.resetForm();
        setCampaignToSave(null);
    };

    const ordersToAddColumns = useMemo(
        () => [
            {
                header: "Midia Cad",
                cell: (cell: any) => {
                    const mediaCad = cell.getValue();
                    if (mediaCad) {
                        return <span className="fw-semibold">{cell.getValue()}</span>;
                    }
                    return <span className="badge bg-danger">não encontrado</span>;
                },
                accessorFn: (row: any) => row.publisher ? row.publisher.mediaCad : '',
                enableColumnFilter: false,
            },
            {
                header: "Data Início",
                accessorFn: (row: any) => {
                    return row.startDate ? format(row.startDate, 'dd/MM/yyyy') : '';
                },
                enableColumnFilter: false,

            },
            {
                header: "Data Termino",
                accessorFn: (row: any) => {
                    return row.endDate ? format(row.endDate, 'dd/MM/yyyy') : '';
                },
                enableColumnFilter: false,
            },
            {
                header: "Publisher",
                accessorFn: (row: any) => row.publisher ? row.publisher.name : '',
                cell: (cell: any) => {
                    return cell.getValue() || <span className="badge bg-danger">não encontrado</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: "Placement",
                accessorFn: (row: any) => row.placement ? row.placement.name : '',
                cell: (cell: any) => {
                    return cell.getValue() || <span className="badge bg-danger">não encontrado</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: 'Tipo',
                accessorKey: 'adType',
                cell: (cell: any) => {
                    const adType = cell.getValue().toLowerCase();
                    const badgeColor = (adType === 'display') ? 'primary' : (adType === 'video_audio' ? 'warning': 'danger');
                    return <span className={`badge bg-${badgeColor}`}>{adType.replace('_', '/') || 'não encontrado'}</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: "Segmentação",
                accessorKey: "segmentation",
                enableColumnFilter: false,
            },
            {
                header: "Linha criativa",
                accessorKey: "creativeLine",
                enableColumnFilter: false,
            },
            {
                header: "Visibilidade",
                accessorKey: "viewability",
                cell: (cell: any) => {
                    const row = cell.row.original;
                    return <span>&gt; {row.viewability}%</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: 'Tamanhos',
                accessorKey: 'sizes',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const actionCell: any[] = [];
                    row.sizes?.forEach((size: string) => {
                        actionCell.push(<span
                            className="badge rounded-pill border border-primary text-primary">{size}</span>);
                    });
                    return <div className="d-flex gap-2">{actionCell}</div>;
                },
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                header: "Tipo de compra",
                accessorKey: "pricingModel",
                enableColumnFilter: false,
            },
            {
                header: "Impressões",
                accessorFn: (row: any) => {
                    return row.impressions ? new Intl.NumberFormat('pt-BR').format(row.impressions) : '-';
                },
                enableColumnFilter: false,
            },
            {
                header: 'Erros e Avisos',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const errors = row.errors.length === 0 ? '' : row.errors.map((error: string) => (
                        <span className="badge rounded-pill border border-danger text-danger">{error}</span>
                    ));
                    const warnings = row.warnings.length === 0 ? '' : row.warnings.map((warning: string) => (
                        <span className="badge rounded-pill border border-warning text-warning">{warning}</span>
                    ));
                    return (<>{errors}{warnings}</>);
                },
                enableSorting: true,
                enableColumnFilter: false,
            },

        ],
        []
    );

    // const SUPPORTED_FORMATS = [
    //     "text/csv",
    //     "text/comma-separated-values",
    // ];

    // First Step Form Validation
    const handleFirstStepForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            name: undefined,
            advertiserId: undefined,
            agencyId: undefined,
            adNetworkId: undefined,
            blockedKeywords: [],
            file: undefined,
        },

        validationSchema: Yup.object({
            name: Yup.string().required("Nome da campaign é obrigatório"),
            advertiserId: Yup.number().optional(),
            agencyId: Yup.number().optional(),
            adNetworkId: Yup.number().optional(),
            blockedKeywords: Yup.array(),
            file: Yup.mixed().optional()
            // TODO: As validações abaixo não estão funcionando
            // .test(
            //     "fileFormat",
            //     "Placement de arquivo inválido",
            //     (value: any) => value && SUPPORTED_FORMATS.includes(value.type)
            // )
            // .test(
            //     "fileSize",
            //     "O arquivo é muito grande",
            //     (value: any) => value && value.size <= 2000000
            // ),
        }),
        onSubmit: (values: any) => {
            setFirstStepImportButtonLoader(true);

            const formData = new FormData();
            formData.append('name', values.name);
            
            // Only append optional fields if they have values
            if (values.advertiserId) {
                formData.append('advertiserId', values.advertiserId);
            }
            if (values.agencyId) {
                formData.append('agencyId', values.agencyId);
            }
            if (values.adNetworkId) {
                formData.append('adNetworkId', values.adNetworkId);
            }
            
            const selectedOptions = (document.getElementById('palavras-chave-protegidas-field') as HTMLSelectElement).selectedOptions;
            const palavrasChaveProtegidaIds = Array.from(selectedOptions).map(({ value }) => value);
            if (palavrasChaveProtegidaIds.length > 0) {
                palavrasChaveProtegidaIds.forEach((item: any) => formData.append('blockedKeywords[]', item));
            }

            const file: HTMLInputElement | null = document.querySelector('#file-field');
            if (file && file.files && file.files[0]) {
                formData.append('file', file.files[0]);
            }

            return importCampaign(formData)
                .then((res) => {
                    setCampaignToSave(res);
                    setActiveArrowTab(2);
                })
                .catch(() => {})
                .finally(() => {
                    setFirstStepImportButtonLoader(false);
                });
        },
    });

    // First Step Form Validation
    const handleSecondStepForm = (event: any) => {
        event.preventDefault();

        const campaignsToSaveLenght = campaignToSave?.orders?.length || 0;
        
        if (campaignsToSaveLenght > 0) {
            if (!window.confirm(`Você tem certeza que deseja importar ${campaignsToSaveLenght} pedidos?`)) {
                return;
            }

            const countWarnings = campaignToSave?.orders.filter((order: any) => order.warnings.length > 0).length || 0;
            if (countWarnings && !window.confirm(`Existem ${countWarnings} pedidos com avisos, deseja continuar?`)) {
                return;
            }

            const countErrors = campaignToSave?.orders.filter((order: any) => order.errors.length > 0).length || 0;
            if (countErrors) {
                window.alert(`Existem ${countErrors} pedidos com erros, não é possível continuar.`)
                return;
            }
        } else {
            if (!window.confirm(`Você tem certeza que deseja criar a campanha sem pedidos?`)) {
                return;
            }
        }

        setSecondStepImportButtonLoader(true);
        
        confirmImportCampaign(campaignToSave).then((res: any) => {
            toggleImportCampaignModal();
            toast.success('Campanha criada com sucesso!');
        })
            .catch(() => {
            })
            .finally(() => {
                setSecondStepImportButtonLoader(false);
            });
    };

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleImportCampaignModal} fade={false} keyboard={true}
               backdrop={false} centered size={"xl"}>
            <ModalHeader toggle={toggleImportCampaignModal} className="bg-light p-3">
                Adicionar campanha
            </ModalHeader>

            <ModalBody>
                <div className="step-arrow-nav mb-4">
                    <Nav
                        className="nav-pills custom-nav nav-justified"
                        role="tablist"
                    >
                        <NavItem>
                            <NavLink
                                href="#"
                                id="steparrow-gen-info-tab"
                                className={`${activeArrowTab === 1 ? "active" : ""} ${activeArrowTab >= 2 ? "done" : ""}`}
                                onClick={() => {
                                    if (activeArrowTab === 2) {
                                        setActiveArrowTab(1);
                                        setCampaignToSave(null);
                                    }
                                }}
                            >
                                Campanha
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                href="#"
                                id="steparrow-gen-info-tab"
                                className={`${activeArrowTab === 2 ? "active" : ""} ${activeArrowTab >= 3 ? "done" : ""}`}
                            >
                                Confirme
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                href="#"
                                id="steparrow-gen-info-tab"
                                className={activeArrowTab === 3 ? "active" : ""}
                            >
                                Concluído
                            </NavLink>
                        </NavItem>
                    </Nav>
                </div>

                <TabContent activeTab={activeArrowTab}>
                    <TabPane id="step-1" tabId={1}>
                        <Form onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFirstStepForm.handleSubmit();
                            return false;
                        }}>
                            <div>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="nome-field"
                                        className="form-label"
                                    >
                                        Nome
                                    </Label>
                                    <Input
                                        className="form-control"
                                        type="text"
                                        id="nome-field"
                                        name="name"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.name}
                                        invalid={
                                            !!(handleFirstStepForm.touched.name && handleFirstStepForm.errors.name)
                                        }
                                    />
                                    {handleFirstStepForm.touched.name && handleFirstStepForm.errors.name ? (
                                        <FormFeedback
                                            type="invalid">{handleFirstStepForm.errors.name}</FormFeedback>
                                    ) : null}
                                </div>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="advertiser-id-field"
                                        className="form-label"
                                    >
                                        Advertiser
                                    </Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        id="advertiser-id-field"
                                        name="advertiserId"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.advertiserId}
                                        invalid={
                                            !!(handleFirstStepForm.touched.advertiserId && handleFirstStepForm.errors.advertiserId)
                                        }
                                    >
                                        <option value="">Selecione um advertiser</option>
                                        {advertisers && advertisers.map((advertiser: any) => (
                                            <option key={advertiser.id} value={advertiser.id}>
                                                {advertiser.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {handleFirstStepForm.touched.advertiserId && handleFirstStepForm.errors.advertiserId ? (
                                        <FormFeedback
                                            type="invalid">{handleFirstStepForm.errors.advertiserId}</FormFeedback>
                                    ) : null}
                                </div>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="agency-id-field"
                                        className="form-label"
                                    >
                                        Agência
                                    </Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        id="agency-id-field"
                                        name="agencyId"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.agencyId}
                                        invalid={
                                            !!(handleFirstStepForm.touched.agencyId && handleFirstStepForm.errors.agencyId)
                                        }
                                    >
                                        <option value="">Selecione uma agência</option>
                                        {agencies && agencies.map((agency: any) => (
                                            <option key={agency.id} value={agency.id}>
                                                {agency.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {handleFirstStepForm.touched.agencyId && handleFirstStepForm.errors.agencyId ? (
                                        <FormFeedback
                                            type="invalid">{handleFirstStepForm.errors.agencyId}</FormFeedback>
                                    ) : null}
                                </div>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="ad-network-id-field"
                                        className="form-label"
                                    >
                                        Ad Network
                                    </Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        id="ad-network-id-field"
                                        name="adNetworkId"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.adNetworkId}
                                        invalid={
                                            !!(handleFirstStepForm.touched.adNetworkId && handleFirstStepForm.errors.adNetworkId)
                                        }
                                    >
                                        <option value="">Selecione uma ad network</option>
                                        {adNetworks && adNetworks.map((adNetwork: any) => (
                                            <option key={adNetwork.id} value={adNetwork.id}>
                                                {adNetwork.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {handleFirstStepForm.touched.adNetworkId && handleFirstStepForm.errors.adNetworkId ? (
                                        <FormFeedback
                                            type="invalid">{handleFirstStepForm.errors.adNetworkId}</FormFeedback>
                                    ) : null}
                                </div>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="palavras-chave-protegidas-field"
                                        className="form-label"
                                    >
                                        Proteções: Lista de palavras
                                    </Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        multiple
                                        id="palavras-chave-protegidas-field"
                                        name="blockedKeywords"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.blockedKeywords}
                                        invalid={
                                            !!(handleFirstStepForm.touched.blockedKeywords && handleFirstStepForm.errors.blockedKeywords)
                                        }
                                    >
                                        {palavrasChaveProtegida && palavrasChaveProtegida.map((palavraChave) => (
                                            <option key={palavraChave.id} value={palavraChave.id}>
                                                {palavraChave.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {handleFirstStepForm.touched.blockedKeywords && handleFirstStepForm.errors.blockedKeywords ? (
                                        <FormFeedback
                                            type="invalid">{handleFirstStepForm.errors.blockedKeywords}</FormFeedback>
                                    ) : null}
                                </div>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="file-field"
                                        className="form-label"
                                    >
                                        Pedidos
                                    </Label>
                                    <Input
                                        className="form-control"
                                        type="file"
                                        id="file-field"
                                        name="file"
                                        accept=".csv"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.file}
                                        invalid={
                                            !!(handleFirstStepForm.touched.file && handleFirstStepForm.errors.file)
                                        }
                                    />
                                    {handleFirstStepForm.touched.file && handleFirstStepForm.errors.file ? (
                                        <FormFeedback
                                            type="invalid">{handleFirstStepForm.errors.file}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>
                            <div className="d-flex align-items-start gap-3 mt-4">
                                <button
                                    type="submit"
                                    disabled={firstStepImportButtonLoader}
                                    className="btn btn-success btn-label right ms-auto nexttab nexttab"
                                >
                                    <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                                    {firstStepImportButtonLoader && <Spinner size="sm"
                                                                             className="me-2"> Carregando... </Spinner>}
                                    Importar
                                </button>
                            </div>
                        </Form>
                    </TabPane>

                    <TabPane id="step-2" tabId={2}>
                        <div>
                            <div className={'mb-3'}>
                                <p>Nome da campanha: {campaignToSave?.name}</p>
                                {campaignToSave?.advertiser && <p>Nome do advertiser: {campaignToSave.advertiser.name}</p>}
                                {campaignToSave?.agency && <p>Nome da agência: {campaignToSave.agency.name}</p>}
                                {campaignToSave?.adNetwork && <p>Nome da ad network: {campaignToSave.adNetwork.name}</p>}
                            </div>
                            {campaignToSave?.orders && campaignToSave.orders.length > 0 ? (
                                <div className="mb-3">
                                    <TableContainer
                                        divClass="table-responsive"
                                        columns={(ordersToAddColumns || [])}
                                        data={(campaignToSave?.orders || [])}
                                        isGlobalFilter={true}
                                        customPageSize={30}
                                        SearchPlaceholder="Procurar..."
                                    />
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <div className="alert alert-info">
                                        <i className="ri-information-line me-2"></i>
                                        Nenhum pedido foi importado. A campanha será criada sem pedidos.
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="d-flex align-items-start gap-3 mt-4">
                            <button
                                type="button"
                                className="btn btn-light btn-label previestab"
                                onClick={() => {
                                    setActiveArrowTab(activeArrowTab - 1);
                                    setCampaignToSave(null);
                                }}
                            >
                                <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>
                                Cancelar & Voltar
                            </button>
                            <button
                                type="button"
                                disabled={secondStepImportButtonLoader}
                                onClick={handleSecondStepForm}
                                className="btn btn-success btn-label right ms-auto nexttab nexttab"
                            >
                                <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                                {secondStepImportButtonLoader && <Spinner size="sm"
                                                                          className="me-2"> Carregando... </Spinner>}
                                Confirmar
                            </button>
                        </div>
                    </TabPane>

                    <TabPane id="step-3" tabId={3}>
                        <div className="text-center">
                            <div className="avatar-md mt-5 mb-4 mx-auto">
                                <div
                                    className="avatar-title bg-light text-success display-4 rounded-circle">
                                    <i className="ri-checkbox-circle-fill"></i>
                                </div>
                            </div>
                            <h5>Muito bom !</h5>
                            <p className="text-muted">
                                Suas campaigns foram cadastradas com sucesso
                            </p>
                            <p>&nbsp;</p>
                            <button
                                type="button"
                                className="btn btn-success btn-label right ms-auto nexttab nexttab"
                                onClick={toggleImportCampaignModal}
                            >
                                <i className="ri-close-line label-icon align-middle fs-16 ms-2"></i>
                                Fechar
                            </button>
                        </div>
                    </TabPane>
                </TabContent>
            </ModalBody>
        </Modal>
    );
};

export default ImportCampaignModal;